import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'app/auth/auth.service';
import { DashboardService } from 'app/dashboard/dashboard.service';
import { Subject } from 'rxjs';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-excel-ratelist',
  templateUrl: './excel-ratelist.component.html',
  styleUrls: ['./excel-ratelist.component.scss'],
})
export class ExcelRateListComponent implements OnInit {
  isExcelFile: boolean = false;
  excelArray: any[] = [];
  dataSheet = new Subject();
  @ViewChild('inputFile', { static: false }) inputFile: ElementRef;
  processedData: any[] = [];
  errorMessage: string = '';
  warningMessage: string;

  constructor(
    public dashboardService: DashboardService,
    public authService: AuthService,
    public dialogRef: MatDialogRef<ExcelRateListComponent>
  ) {}

  ngOnInit(): void {}

  closedialog() {
    this.dialogRef.close([]);
  }

  onFileChange(evt: any) {
    this.excelArray = [];
    this.processedData = [];
    this.errorMessage = '';

    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) {
      this.dashboardService.openSnackBar('Please select a single Excel file.', 'OK');
      return;
    }

    const file: File = target.files[0];
    this.isExcelFile = !!file.name.match(/(.xls|.xlsx)$/);
    if (!this.isExcelFile) {
      this.dashboardService.openSnackBar('File format not supported. Please upload an Excel file.', 'OK');
      return;
    }

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      const bstr: string = e.target.result;
      let workBook: XLSX.WorkBook;

      try {
        workBook = XLSX.read(bstr, { type: 'binary' });
      } catch (error) {
        this.dashboardService.openSnackBar('Error reading Excel file. Please ensure the file is not corrupted.', 'OK');
        return;
      }

      const jsonData: { [key: string]: any[] } = workBook.SheetNames.reduce((initial, name) => {
        const sheet = workBook.Sheets[name];
        initial[name] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        return initial;
      }, {});

      for (let sheetName of workBook.SheetNames) {
        const sheetData = jsonData[sheetName];
        if (sheetData && sheetData.length > 0) {
          this.excelArray.push(...sheetData);
        }
      }
      this.processExcelData();
    };

    reader.onerror = () => {
      this.dashboardService.openSnackBar('Unable to read ' + file.name, 'OK');
    };

    reader.readAsBinaryString(file);
  }

  processExcelData() {
    const requiredColumns = [
      'Service ID',
      'Service Name',
      'Code',
      'General',
      'Semi Pvt.',
      'Pvt.',
      'Semi Deluxe',
      'Deluxe',
      'Ser. Charge',
      'Disc. Type',
      'Disc. Percent',
      'Max. Disc',
    ];
  
    if (!this.excelArray || this.excelArray.length === 0) {
      this.errorMessage = 'No data found in the Excel file.';
      this.warningMessage = '';
      this.dashboardService.openSnackBar(this.errorMessage, 'OK');
      return;
    }
  
    const headers = Object.keys(this.excelArray[0]).map(h => h.trim());
    const trimmedRequiredColumns = requiredColumns.map(col => col.trim());
    const missingColumns = trimmedRequiredColumns.filter(
      col => headers.indexOf(col) === -1
    );
  
    if (missingColumns.length === trimmedRequiredColumns.length) {
      this.errorMessage = 'None of the required columns were found.';
      this.warningMessage = '';
      this.dashboardService.openSnackBar(this.errorMessage, 'OK');
      return;
    }
  
    if (missingColumns.length > 0) {
      this.errorMessage = '';
      this.warningMessage = 'Note: Missing columns - ' + missingColumns.join(', ') + '. Proceeding with available columns.';
      this.dashboardService.openSnackBar(this.warningMessage, 'OK');
    } else {
      this.errorMessage = '';
      this.warningMessage = '';
    }
  
    const invalidRows = this.excelArray.filter(row => !row['Service ID'] || !row['Service Name']);
    if (invalidRows.length > 0) {
      this.errorMessage = 'Some rows are missing mandatory columns (Service ID or Service Name).';
      this.warningMessage = '';
      this.dashboardService.openSnackBar(this.errorMessage, 'OK');
      return;
    }
  
    this.processedData = this.excelArray.map((row) => {
      return {
        serviceId: row['Service ID'] || '',
        code: row['Code'] || '',
        serviceName: row['Service Name'] || '',
        general: row['General'] || '',
        semiPvt: row['Semi Pvt.'] || '',
        pvt: row['Pvt.'] || '',
        semiDeluxe: row['Semi Deluxe'] || '',
        deluxe: row['Deluxe'] || '',
        serviceCharge: row['Ser. Charge'] || '',
        discType: row['Disc. Type'] || '',
        discPercent: row['Disc. Percent'] || '',
        maxDisc: row['Max. Disc'] || '',
      };
    });
  }
  
  
  transferDataToRateList() {
    if (!this.processedData || this.processedData.length === 0) {
      this.dashboardService.openSnackBar(
        'No data found. Please upload a valid Excel file first.',
        'OK'
      );
      return;
    }
    this.dialogRef.close(this.processedData);
  }
}
